import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useRecordStockCountsMutation, useStockCountActionMutation, useStockCountItemsQuery,
  useStockCountQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, ErrorState, Input, LoadingState, Modal, Pagination, Select,
  Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { COUNT_TONE } from './StockCountsPage';

/**
 * The count sheet.
 *
 * Counts are entered against the frozen `expectedQty` on each line and saved in
 * batches, so a long sheet survives a dropped connection without losing the
 * whole session's work.
 */
export function StockCountDetailModal({
  countId,
  onClose,
}: {
  countId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'all' | 'uncounted' | 'variance'>('all');

  const { data: count, isLoading, isError, error, refetch } = useStockCountQuery(countId);
  const { data: items, isFetching } = useStockCountItemsQuery({
    id: countId,
    params: {
      page,
      limit: 25,
      uncountedOnly: filter === 'uncounted' || undefined,
      varianceOnly: filter === 'variance' || undefined,
    },
  });

  const [recordCounts, recordState] = useRecordStockCountsMutation();
  const [runAction, actionState] = useStockCountActionMutation();

  /** Counts typed but not yet saved, keyed by line id. */
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const status = count?.status;
  const editable = status === 'IN_PROGRESS';
  const pendingLines = Object.entries(draft).filter(([, value]) => value !== '');

  const save = async (): Promise<void> => {
    setActionError(null);
    try {
      await recordCounts({
        id: countId,
        lines: pendingLines.map(([itemId, value]) => ({ itemId, countedQty: Number(value) })),
      }).unwrap();
      setDraft({});
    } catch (failure) {
      setActionError(apiErrorMessage(failure, 'Those counts could not be saved'));
    }
  };

  const act = async (action: string): Promise<void> => {
    setActionError(null);
    try {
      await runAction({ id: countId, action }).unwrap();
    } catch (failure) {
      // Submitting refuses while lines are uncounted; the message says how many.
      setActionError(apiErrorMessage(failure, `The count could not be ${action}ed`));
    }
  };

  const progress = count?.progress;

  return (
    <Modal
      open
      onClose={onClose}
      title={count ? `Stock count ${count.number}` : 'Stock count'}
      description={count ? `${count.warehouse.name} · ${count.scope.toLowerCase()} count` : undefined}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>

          {editable && (
            <Can permission={P.STOCK_COUNT}>
              <Button
                variant="outline"
                loading={recordState.isLoading}
                disabled={pendingLines.length === 0}
                onClick={() => void save()}
              >
                Save {pendingLines.length > 0 ? `${pendingLines.length} count(s)` : 'counts'}
              </Button>
              <Button
                loading={actionState.isLoading}
                disabled={pendingLines.length > 0}
                title={pendingLines.length > 0 ? 'Save your counts first' : undefined}
                onClick={() => void act('submit')}
              >
                Submit for approval
              </Button>
            </Can>
          )}

          {status === 'PENDING_APPROVAL' && (
            <Can permission={P.STOCK_COUNT_APPROVE}>
              <Button loading={actionState.isLoading} onClick={() => void act('approve')}>
                Approve and post
              </Button>
            </Can>
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : count ? (
        <div className="space-y-4">
          {actionError && <Alert tone="danger">{actionError}</Alert>}

          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={COUNT_TONE[count.status]}>{count.status.toLowerCase()}</Badge>
            {progress && (
              <span className="text-xs text-muted">
                {progress.counted} of {progress.total} lines counted
              </span>
            )}
            <span className="text-xs text-muted">
              Variance value {formatMoney(count.totalVarianceValue, format)}
            </span>
          </div>

          {status === 'PENDING_APPROVAL' && (
            <Alert tone="warning">
              Approving posts every variance to the ledger as a stock adjustment. Nothing has
              changed the stock figures yet.
            </Alert>
          )}

          <div className="w-56">
            <Select
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as 'all' | 'uncounted' | 'variance');
                setPage(1);
              }}
            >
              <option value="all">All lines</option>
              <option value="uncounted">Not counted yet</option>
              <option value="variance">With a variance</option>
            </Select>
          </div>

          <Table>
            <thead>
              <tr>
                <Th>Product</Th>
                <Th numeric>System</Th>
                <Th numeric>Counted</Th>
                <Th numeric>Variance</Th>
                <Th numeric>Value</Th>
              </tr>
            </thead>
            <tbody className={isFetching ? 'opacity-60' : undefined}>
              {items?.items.map((item) => {
                const typed = draft[item.id];
                const variance =
                  typed !== undefined && typed !== ''
                    ? String(Number(typed) - Number(item.expectedQty))
                    : item.countedQty === null
                      ? null
                      : item.varianceQty;

                return (
                  <tr key={item.id}>
                    <Td>
                      <p>{item.variant.product.name}</p>
                      <p className="text-xs text-muted">
                        {item.variant.sku}
                        {item.batch ? ` · batch ${item.batch.batchNumber}` : ''}
                      </p>
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatQuantity(item.expectedQty, format)}
                    </Td>
                    <Td numeric>
                      {editable ? (
                        <Can
                          permission={P.STOCK_COUNT}
                          fallback={
                            <span>
                              {item.countedQty === null
                                ? '—'
                                : formatQuantity(item.countedQty, format)}
                            </span>
                          }
                        >
                          <Input
                            type="number"
                            min="0"
                            step="0.0001"
                            className="h-9 w-24 text-right"
                            placeholder={item.countedQty ?? '—'}
                            value={typed ?? ''}
                            onChange={(event) =>
                              setDraft({ ...draft, [item.id]: event.target.value })
                            }
                          />
                        </Can>
                      ) : item.countedQty === null ? (
                        '—'
                      ) : (
                        formatQuantity(item.countedQty, format)
                      )}
                    </Td>
                    <Td
                      numeric
                      className={
                        variance === null || Number(variance) === 0
                          ? 'text-secondary'
                          : Number(variance) < 0
                            ? 'text-red-600'
                            : 'text-emerald-600'
                      }
                    >
                      {variance === null ? '—' : formatQuantity(variance, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {item.countedQty === null ? '—' : formatMoney(item.varianceValue, format)}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {items && (
            <Pagination
              page={items.meta.page}
              totalPages={items.meta.totalPages}
              total={items.meta.total}
              onPageChange={setPage}
            />
          )}
        </div>
      ) : null}
    </Modal>
  );
}
